<template>
  <div class="flex flex-col h-full min-h-0 w-full gap-2">
    <!-- Search + upload stay pinned; only the list below scrolls -->
    <div class="flex flex-col gap-2 flex-none">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText size="small" class="w-full" v-model="fileSearchQuery" placeholder="חיפוש קובץ" />
      </IconField>
      <label
        :for="inputId"
        class="flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-white hover:bg-gray-100 p-3"
      >
        <div v-if="!uploadInProgress" class="flex items-center justify-center gap-2">
          <i class="pi pi-plus text-gray-500" />
          <span class="text-sm text-center text-nowrap">העלאת קבצים</span>
        </div>
        <loader v-else />
        <input
          :id="inputId"
          type="file"
          class="hidden"
          accept="image/*,application/pdf"
          multiple
          @change="onSelect"
        />
      </label>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto pb-6">
      <div v-if="filteredFiles.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
        <div
          v-for="file of filteredFiles"
          :key="file.url"
          class="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-md shadow-sm hover:border-gray-300 transition-colors"
        >
          <Image
            v-if="isImage(file)"
            :src="file.url"
            :alt="file.name"
            class="w-12 h-12 flex-none rounded-lg overflow-hidden [&_img]:w-12 [&_img]:h-12 [&_img]:object-cover [&_img]:rounded-lg"
            preview
          />
          <div
            v-else
            class="w-12 h-12 flex-none rounded-lg bg-primary-50 flex items-center justify-center"
          >
            <i class="pi text-primary text-lg" :class="fileIcon(file)" />
          </div>

          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium truncate" :title="file.name">{{ file.name }}</span>
            <span class="text-xs text-gray-500">{{ formatDate(file.uploadedAt) }}</span>
          </div>

          <div class="flex items-center gap-1 flex-none">
            <i class="pi pi-download p-2 cursor-pointer" @click="downloadFile(file)" />
            <i class="pi pi-trash p-2 text-red-500 cursor-pointer" @click="confirmRemoveFile(file)" />
          </div>
        </div>
      </div>
      <div v-else class="w-full flex flex-col justify-center items-center gap-2 pt-8">
        <i class="pi pi-file text-4xl text-gray-300"></i>
        <span class="text-sm text-gray-500">
          {{ fileSearchQuery ? 'לא נמצאו קבצים' : 'עדיין לא הועלו קבצים' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {saveFileFromUrl} from '@app/utils/save-file';
import {useToast} from "vue-toastification";
import {computed, PropType, ref} from "vue";
import type {ICustomer, ICustomerFile} from "@torim-app/types";
import {CustomersApi} from "@app/api/admin";
import {DateTime} from "luxon";
import {generateRandomId} from "@app/utils/strings";
import { trackEvent } from '@app/composables/posthog';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { useConfirm } from 'primevue/useconfirm';

const toast = useToast();
// Uses the shared ConfirmationService; the dialog is rendered once by Customer.vue.
const confirm = useConfirm();

const props = defineProps({
  customer: {
    type: Object as PropType<ICustomer>,
    required: true
  },
  modelValue: {
    type: Array as PropType<ICustomerFile[]>,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'filesChanged', 'refresh']);

const uploadInProgress = ref(false);
const fileSearchQuery = ref();
const selectedFiles = ref<File[]>([]);
const inputId = generateRandomId(10);

const files = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
    emit('filesChanged', value);
  }
});

const filteredFiles = computed(() => {
  if (!fileSearchQuery.value) return files.value;

  return files.value.filter(file =>
    file.name.toLowerCase().includes(String(fileSearchQuery.value).toLowerCase())
  );
});

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'avif'];

// `type` holds the mime subtype set on upload (e.g. "jpeg", "pdf"), but older
// records may not have it — fall back to the file extension.
const fileExtension = (file: ICustomerFile) => (file.type || file.name.split('.').pop() || '').toLowerCase();

const isImage = (file: ICustomerFile) => IMAGE_TYPES.includes(fileExtension(file));

const fileIcon = (file: ICustomerFile) => {
  const ext = fileExtension(file);
  if (ext === 'pdf') return 'pi-file-pdf';
  if (ext.includes('word') || ext === 'doc' || ext === 'docx') return 'pi-file-word';
  if (ext === 'xls' || ext === 'xlsx' || ext.includes('sheet')) return 'pi-file-excel';
  return 'pi-file';
};

const formatDate = (date: Date | string) => {
  const dt = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : '';
};

const onSelect = (event: any) => {
  selectedFiles.value = event.target.files;
  handleUpload(event.target.files);
};

const handleUpload = async (filesToUpload: File[]) => {
  try {
    if (filesToUpload.length > 0) {
      uploadInProgress.value = true;
      try {
        const formData = new FormData();
        for (const fileToUpload of filesToUpload) {
          const blob = new Blob([fileToUpload], {type: fileToUpload.type});
          const newFile = new File([blob], fileToUpload.name, {
            type: fileToUpload.type,
            lastModified: fileToUpload.lastModified
          });
          formData.append('files', newFile, fileToUpload.name);
        }
        const uploadedFiles = await CustomersApi.uploadFiles(formData, props.customer.id!);
        console.log(uploadedFiles)
        files.value = [...files.value, ...uploadedFiles];
        selectedFiles.value.splice(0);
        emit('refresh');
      } catch (e) {
        console.error(e);
      } finally {
        uploadInProgress.value = false;
      }
    }
    trackEvent('customer_file_uploaded', { count: filesToUpload.length });
    toast.success('הקבצים הועלו בהצלחה');
  } catch (error) {
    toast.error('שגיאה בהעלאת הקבצים');
    console.error(error);
  }
};

const confirmRemoveFile = (file: ICustomerFile) => {
  confirm.require({
    message: `למחוק את הקובץ "${file.name}"?`,
    header: 'מחיקת קובץ',
    acceptLabel: 'מחיקה',
    rejectLabel: 'ביטול',
    acceptClass: 'p-button-danger',
    accept: () => removeFile(file)
  });
};

const removeFile = async (file: ICustomerFile) => {
  try {
    await CustomersApi.deleteFile(props.customer.id!, file.url);
    // Optimistic removal for instant feedback, then re-sync from the server.
    files.value = files.value.filter(f => f.url !== file.url);
    emit('refresh');
    trackEvent('customer_file_deleted', { file_type: file.name.split('.').pop() });
    toast.success('הקובץ נמחק בהצלחה');
  } catch (e) {
    console.error(e);
    toast.error('שגיאה במחיקת הקובץ');
  }
};

const downloadFile = async (file: ICustomerFile) => {
  trackEvent('customer_file_downloaded', { file_type: file.name.split('.').pop() });
  try {
    const result = await saveFileFromUrl(file.url, file.name);
    if (result === 'downloaded') toast.success('הקובץ הורד בהצלחה');
  } catch (error) {
    toast.error('שגיאה בהורדת הקובץ');
    console.error(error);
  }
};
</script>

<style scoped lang="scss">
</style>
